import { WHEEL_CONFIG } from '../constants';

/**
 * Generate crystal vibe colors with linear progression from orange to purple
 * @param {number} count - Number of colors to generate
 * @returns {Array<string>} - Array of RGB color strings
 */
export const generateCrystalColors = (count) => {
  if (count <= 0) return [];
  
  const colors = [];
  for (let i = 0; i < count; i++) {
    const progress = i / (count - 1); // 0 to 1
    
    // Create smooth transition from orange to purple
    const r = Math.round(255 * (1 - progress * 0.3)); // 255 to ~178
    const g = Math.round(165 * (1 - progress * 0.8)); // 165 to ~33
    const b = Math.round(0 + progress * 128); // 0 to 128
    
    colors.push(`rgb(${r}, ${g}, ${b})`);
  }
  return colors;
};

/**
 * Calculate wheel segment properties
 * @param {number} totalSegments - Total number of segments
 * @param {number} index - Segment index
 * @returns {Object} - Segment properties
 */
export const calculateSegmentProperties = (totalSegments, index) => {
  const segmentAngle = 360 / totalSegments;
  const startAngle = (index * segmentAngle - 90) * Math.PI / 180;
  const endAngle = ((index + 1) * segmentAngle - 90) * Math.PI / 180;
  const midAngle = startAngle + (endAngle - startAngle) / 2;
  
  return {
    segmentAngle,
    startAngle,
    endAngle,
    midAngle,
  };
};

/**
 * Generate SVG path data for a pie slice
 * @param {number} center - Center point coordinate
 * @param {number} radius - Wheel radius
 * @param {number} startAngle - Start angle in radians
 * @param {number} endAngle - End angle in radians
 * @param {number} segmentAngle - Segment angle in degrees
 * @returns {string} - SVG path data
 */
export const generatePieSlicePath = (center, radius, startAngle, endAngle, segmentAngle) => {
  const x1 = center + radius * Math.cos(startAngle);
  const y1 = center + radius * Math.sin(startAngle);
  const x2 = center + radius * Math.cos(endAngle);
  const y2 = center + radius * Math.sin(endAngle);
  
  const largeArcFlag = segmentAngle > 180 ? 1 : 0;
  
  return [
    `M ${center} ${center}`,
    `L ${x1} ${y1}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
    'Z'
  ].join(' ');
};

/**
 * Calculate text position for wheel segments
 * @param {number} center - Center point coordinate
 * @param {number} radius - Wheel radius
 * @param {number} midAngle - Middle angle of the segment
 * @param {number} textOffset - Text offset from center (0.6 = 60% from center)
 * @returns {Object} - Text position coordinates
 */
export const calculateTextPosition = (center, radius, midAngle, textOffset = 0.6) => {
  return {
    x: center + (radius * textOffset) * Math.cos(midAngle),
    y: center + (radius * textOffset) * Math.sin(midAngle),
  };
};

/**
 * Generate random spin parameters
 * @returns {Object} - Spin parameters
 */
export const generateSpinParameters = () => {
  const spins = WHEEL_CONFIG.MIN_SPINS + Math.random() * (WHEEL_CONFIG.MAX_SPINS - WHEEL_CONFIG.MIN_SPINS);
  return {
    spins,
    duration: WHEEL_CONFIG.SPIN_DURATION,
  };
};
